import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const SPLIDE_CSS = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css';
const SPLIDE_JS = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js';

/** @type {Promise<void> | null} */
let splideLoader = null;

/**
 * Loads Splide CSS/JS once per page.
 * @returns {Promise<void>}
 */
function loadSplide() {
  if (window.Splide) return Promise.resolve();
  if (splideLoader) return splideLoader;

  splideLoader = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${SPLIDE_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = SPLIDE_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${SPLIDE_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Splide failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = SPLIDE_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Splide failed to load'));
    document.head.appendChild(script);
  });

  return splideLoader;
}

/**
 * @param {string} str
 * @returns {number}
 */
function parseInches(str) {
  if (!str) return NaN;
  const normalized = String(str)
    .replace(/½/g, '.5')
    .replace(/¼/g, '.25')
    .replace(/¾/g, '.75')
    .replace(/⅛/g, '.125')
    .replace(/⅜/g, '.375')
    .replace(/⅝/g, '.625')
    .replace(/⅞/g, '.875')
    .trim();
  return Number.parseFloat(normalized);
}

/**
 * @param {number} value
 * @returns {string}
 */
function formatInches(value) {
  if (Number.isNaN(value)) return '';
  const intPart = Math.floor(value);
  const decimal = value - intPart;
  /** @type {Record<number, string>} */
  const fractions = {
    0.5: '½',
    0.25: '¼',
    0.75: '¾',
    0.125: '⅛',
    0.375: '⅜',
    0.625: '⅝',
    0.875: '⅞',
  };

  for (const [key, glyph] of Object.entries(fractions)) {
    if (Math.abs(decimal - Number(key)) < 0.01) {
      return `${intPart}${glyph}`;
    }
  }

  if (Math.abs(decimal) < 0.01) return String(intPart);
  return value.toFixed(2).replace(/\.00$/, '');
}

/**
 * Product showcase: vertical Splide, sizing unit tabs, mobile color select sync.
 *
 * @typedef {Object} ProductShowcaseRefs
 * @property {HTMLElement} [gallery]
 * @extends {Component<ProductShowcaseRefs>}
 */
class ProductShowcase extends Component {
  /** @type {import('@splidejs/splide').default | null} */
  #splide = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroySplide();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#destroySplide();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    document.documentElement.style.removeProperty('--pdp-sticky-buy-height');
  }

  async #init() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#bindSizing();
    this.#bindColorSelect();
    this.#bindStockTracker();
    this.#observeStickyHeight();
    await this.#initSplide();
  }

  #bindStockTracker() {
    // Handled by dynamic stock tracker with real-time admin and sold-out sync
  }

  async #initSplide() {
    const root = this.querySelector('[data-pdp-splide]');
    if (!(root instanceof HTMLElement)) return;

    try {
      await loadSplide();
    } catch {
      return;
    }

    if (!window.Splide || this.#splide) return;

    const desktopHeight = this.#desktopGalleryHeight();
    const peekPercent = Number.parseFloat(this.dataset.galleryPeek || '20');
    const peekBreakpoint = Number.parseInt(this.dataset.galleryPeekMin || '1440', 10);
    const peekMinWidth = Number.isFinite(peekBreakpoint) ? peekBreakpoint : 1440;
    const peekPerPage =
      Number.isFinite(peekPercent) && peekPercent > 0 ? 1 + peekPercent / 100 : 1;

    root.style.setProperty('--pdp-gallery-track-height', desktopHeight);
    this.style.setProperty('--pdp-gallery-track-height', desktopHeight);

    // Mobile-first:
    // < 990: horizontal, 1 per view
    // 990–peekMin: vertical, 1 per view
    // > peekMin: vertical, 1 + peek of next slide
    const slideSpeed = 400;

    this.#splide = new window.Splide(root, {
      mediaQuery: 'min',
      direction: 'ltr',
      autoHeight: false,
      perPage: 1,
      perMove: 1,
      gap: 0,
      arrows: false,
      pagination: false,
      wheel: false,
      releaseWheel: true,
      drag: true,
      snap: true,
      trimSpace: true,
      focus: 0,
      speed: slideSpeed,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
      updateOnMove: true,
      breakpoints: {
        990: {
          direction: 'ttb',
          height: desktopHeight,
          perPage: 1,
          perMove: 1,
          gap: '0.5rem',
          pagination: false,
          wheel: false,
          releaseWheel: true,
          trimSpace: true,
          speed: slideSpeed,
        },
        [peekMinWidth + 1]: {
          direction: 'ttb',
          height: desktopHeight,
          perPage: peekPerPage,
          perMove: 1,
          gap: '0.5rem',
          pagination: false,
          wheel: false,
          releaseWheel: true,
          trimSpace: false,
          speed: slideSpeed,
        },
      },
    });

    this.#splide.mount();
    this.#bindGalleryWheel(root);
  }

  /**
   * One slide per wheel gesture over the middle gallery (page stays still).
   * Further wheel at the first/last slide scrolls the page.
   * @param {HTMLElement} root
   */
  #bindGalleryWheel(root) {
    const gallery = this.querySelector('.pdp-showcase__gallery');
    const target = gallery instanceof HTMLElement ? gallery : root;
    const signal = this.#abortController?.signal;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let settleTimer = null;
    let gestureLocked = false;

    const unlockGesture = () => {
      gestureLocked = false;
      settleTimer = null;
    };

    const clearSettleTimer = () => {
      if (settleTimer !== null) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
    };

    // Listener is on the middle gallery only — wheel over info/buy scrolls the page normally
    target.addEventListener(
      'wheel',
      (event) => {
        if (!this.#splide) return;
        // Desktop vertical gallery only — mobile stays free to page-scroll / drag
        if (!window.matchMedia('(min-width: 990px)').matches) return;

        const deltaY = event.deltaY;
        if (deltaY === 0) return;

        const index = this.#splide.index;
        const end = this.#splide.Components?.Controller?.getEnd?.() ?? this.#splide.length - 1;
        const scrollingDown = deltaY > 0;
        const atStart = index <= 0;
        const atEnd = index >= end;

        // Only release the page once the gallery truly cannot move further
        if (scrollingDown && atEnd) {
          clearSettleTimer();
          unlockGesture();
          return;
        }
        if (!scrollingDown && atStart) {
          clearSettleTimer();
          unlockGesture();
          return;
        }

        // Always block page scroll while slides can still move (including small trackpad deltas)
        event.preventDefault();
        event.stopPropagation();

        // One slide per scroll gesture; ignore the rest of the same wheel burst
        if (!gestureLocked) {
          gestureLocked = true;
          this.#splide.go(scrollingDown ? '>' : '<');
        }

        // Unlock after the wheel burst settles so the next intentional scroll can move once
        clearSettleTimer();
        settleTimer = setTimeout(unlockGesture, 180);
      },
      { passive: false, signal }
    );

    signal?.addEventListener(
      'abort',
      () => {
        clearSettleTimer();
        unlockGesture();
      },
      { once: true }
    );
  }

  #desktopGalleryHeight() {
    const custom = this.dataset.galleryHeight;
    if (custom) return custom;
    return '70vh';
  }

  #destroySplide() {
    if (this.#splide) {
      this.#splide.destroy(true);
      this.#splide = null;
    }
  }

  #bindSizing() {
    const signal = this.#abortController?.signal;

    this.querySelectorAll('[data-pdp-sizing]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      const table = root.querySelector('[data-sizing-table]');
      table?.querySelectorAll('td').forEach((cell) => {
        if (!(cell instanceof HTMLElement)) return;
        if (!cell.dataset.original) {
          cell.dataset.original = cell.textContent?.trim() ?? '';
        }
      });

      root.querySelectorAll('[data-unit]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            if (!(button instanceof HTMLElement)) return;
            const unit = button.dataset.unit;
            if (!unit) return;
            this.#setSizingUnit(root, unit);
          },
          { signal }
        );
      });

      const defaultUnit = root.dataset.defaultUnit || 'IN';
      this.#setSizingUnit(root, defaultUnit);
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {string} unit
   */
  #setSizingUnit(root, unit) {
    root.querySelectorAll('[data-unit]').forEach((button) => {
      const active = button instanceof HTMLElement && button.dataset.unit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    const measurePanel = root.querySelector('[data-sizing-panel="measure"]');
    const intlPanel = root.querySelector('[data-sizing-panel="intl"]');

    if (unit === 'INTL') {
      measurePanel?.setAttribute('hidden', '');
      intlPanel?.removeAttribute('hidden');
      return;
    }

    intlPanel?.setAttribute('hidden', '');
    measurePanel?.removeAttribute('hidden');

    root.querySelectorAll('[data-sizing-table] td').forEach((cell) => {
      if (!(cell instanceof HTMLElement)) return;
      const original = cell.dataset.original ?? '';
      const inches = parseInches(original);
      if (Number.isNaN(inches)) return;

      if (unit === 'CM') {
        cell.textContent = (inches * 2.54).toFixed(2);
      } else {
        cell.textContent = original || formatInches(inches);
      }
    });
  }

  #bindColorSelect() {
    const signal = this.#abortController?.signal;
    const colorSelect = this.querySelector('[data-pdp-color-select]');
    if (!(colorSelect instanceof HTMLSelectElement)) return;

    colorSelect.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        const value = colorSelect.value;
        const radios = this.querySelectorAll('.pdp-buy__option--swatches input[type="radio"]');
        for (const radio of radios) {
          if (!(radio instanceof HTMLInputElement)) continue;
          if (radio.value === value) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      },
      { signal }
    );

    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.closest('.pdp-buy__option--swatches')) return;
        if (target.checked) {
          colorSelect.value = target.value;
        }
      },
      { signal }
    );
  }

  #observeStickyHeight() {
    const buy = this.querySelector('.pdp-showcase__buy');
    if (!(buy instanceof HTMLElement)) return;

    const setHeight = () => {
      const height = `${buy.offsetHeight}px`;
      // Apply to document so clearance sits at page end, not inside this section
      document.documentElement.style.setProperty('--pdp-sticky-buy-height', height);
      this.style.setProperty('--pdp-sticky-buy-height', height);
    };

    setHeight();
    this.#resizeObserver = new ResizeObserver(setHeight);
    this.#resizeObserver.observe(buy);
  }
}

if (!customElements.get('product-showcase')) {
  customElements.define('product-showcase', ProductShowcase);
}

export default ProductShowcase;
